import React, { useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import type { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import Button, { ILink } from 'tbk-components/src/components/Button';
import Nav, { NavItem } from 'tbk-components/src/components/Nav';
import AlertBarModified from '../AlertBarModified';
import Icon from 'tbk-components/src/components/Icon';
import Image from 'tbk-components/src/components/Image';

// reuse IconLink interface from linkGroup
import type { IIconLink } from '../LinkGroup';
import AccordionNav from './AccordionNav';
import logo from '../../assets/logo-symbol-black.svg';
import SearchTool from '../SearchTool';
import { useTransition } from '../../utils';
import SocialIcons, { SocialIcon } from '../SocialIcons';

export interface INavMobileProps extends IBaseElementProps {
	alertLink?: any;
	ctaButton?: IIconLink;
	mobileMenu?: NavItem[];
	loginMenu?: NavItem[];
	secondaryMenu?: NavItem[];
	homeURL?: string;
	socialIcons?: SocialIcon[];
}

/**
 * @requiresHooks
 */
const NavMobile: React.FC<INavMobileProps> = ({
	alertLink,
	mobileMenu,
	ctaButton,
	loginMenu,
	secondaryMenu,
	homeURL,
	socialIcons = [],
}) => {
	// use custom hook
	const [openMenu, menuTransition, setOpenMenu] = useTransition();
	const [translationLanguages, setTranslationLanguages] = useState(null);
	const [selectedLanguage, setSelectedLanguage] = useState(undefined);
	const [YVal, setYVal] = useState(0);

	const containerRef = useRef();
	const menuRef = useRef();

	// calculate navMobile container height -  menu bar height. Value is used in mobile menu open animation.
	useEffect(() => {
		if (!containerRef.current || !menuRef.current) {
			return;
		}

		if (openMenu === 'menu') {
			// @ts-ignore
			setYVal(containerRef?.current?.offsetHeight - menuRef?.current?.offsetHeight);
		} else {
			setYVal(0);
		}
	}, [openMenu]);

	// bunch of bools to keep markup DRY
	const menuActive = openMenu === 'menu';
	const loginActive = openMenu === 'login';
	const searchActive = openMenu === 'search';

	// Getting the translation select on the mobile menu turns out to be a manual process.  Google Translate doesn't
	// allow more than one translate widget on the page.  However, we need one in the mobile menu.  So, what we do
	// here is inspect the main translation element and grab all languages in it.  We set a state variable with those
	// languages so we can use it within this component ( see below where we output google_translate_element_mobile ).
	useEffect(() => {
		if (menuActive && !translationLanguages) {
			const select = document.querySelector(
				'#google_translate_element select',
			) as HTMLSelectElement;
			if (select) {
				const languages = [];
				let hasEnglish = false;
				select
					.querySelectorAll('option')
					.forEach((el: HTMLOptionElement) => {
						languages.push({
							value: el.value,
							label: el.textContent,
						});
						if ('en' === el.value) {
							hasEnglish = true;
						}
					});
				if (!hasEnglish) {
					// The google translate select doesn't have english in it until the language is switched away
					// from english.  This just ensures we always have english in our mobile select.
					languages.push({ value: 'en', label: 'English' });
					languages.sort(function (a, b) {
						if (!a.value) {
							// This is the "Select Language" option
							return -1;
						} else if (!b.value) {
							return 1;
						} else {
							return a.label < b.label
								? -1
								: a.label > b.label
								? 1
								: 0;
						}
					});
				}

				setTranslationLanguages(languages);
				setSelectedLanguage(select.value);
			}
		}
	}, [menuActive, translationLanguages]);

	// When the mobile selectedLanguage changes, we'll update the main translate select element and trigger change.
	useEffect(() => {
		if (typeof selectedLanguage !== 'undefined') {
			const select = document.querySelector(
				'#google_translate_element select',
			) as HTMLSelectElement;
			select.value = selectedLanguage || 'en';
			select.dispatchEvent(new Event('change'));
		}
	}, [selectedLanguage]);

	// component to be passed to SearchTool component
	const searchInput = (props) => (
		<form
			className={cn(
				'navbar-mobile__search',
				searchActive && 'navbar-mobile__search--open',
				searchActive && menuTransition && 'closing',
			)}
			action={homeURL}
		>
			<div className="input-group">
				<button
					className="search-btn"
					type="submit"
					aria-label="Submit search"
				>
					<Icon name="search" />
				</button>
				<input
					{...props}
					className="form-control"
					type="search"
					placeholder="Search"
					aria-label="Search"
					name="s"
				/>
				<button
					className="clear-search"
					type="button"
					onClick={() => {
						// hack that uses the onChange func attached to props to clear the input value.
						// Props in this case is provided by SearchTool, which is just a wrapper for react-autocomplete
						// The onChange func accepts an event obj as it's argument and updates the input value state with whatever is in event.target.value
						props.onChange({ target: { value: '' } });
					}}
				>
					Clear All
				</button>
				<button
					className="close-btn"
					type="button"
					aria-label="Close search"
					onClick={() => {
						setOpenMenu();
					}}
				>
					<Icon name="close" />
				</button>
			</div>
		</form>
	);

	return (
		<nav
			className={cn(
				'navbar-mobile',
				'd-lg-none',
				menuActive && 'navbar-mobile--menu-open',
				menuActive && menuTransition && 'closing',
			)}
			style={{ marginTop: `-${YVal}px` }}
			ref={containerRef}
		>
			{alertLink.length > 0 &&
				alertLink.map((alertBar, index) => {
					return (
						alertBar?.children && (
							<AlertBarModified
								key={index}
								classNames={['navbar__alert']}
								alertHash={alertBar.alertHash}
								isClosable={alertBar.isClosable}
								indexNum={index}
							>
								<div>{alertBar.children}</div>
							</AlertBarModified>
						)
					);
				})}
			{ctaButton && (
				<div className="navbar-mobile__cta hide-on-open">
					<a href={ctaButton.href} target={ctaButton.target}>
						<Icon name={ctaButton.icon} /> {ctaButton.label}
					</a>
				</div>
			)}
			<div className="navbar-mobile__menu" ref={menuRef}>
				{searchActive ? (
					<SearchTool renderInput={searchInput} open={searchActive} />
				) : (
					<div className="navbar-mobile__menu-inner">
						<button
							type="button"
							aria-label="toggle main menu"
							className="menu-btn"
							onClick={() => {
								setOpenMenu('menu');
							}}
						>
							<Icon name={menuActive ? 'close' : 'menu'} />
						</button>
						<button
							className="search-btn"
							type="button"
							aria-label="Show search bar"
							onClick={() => {
								setOpenMenu('search');
							}}
						>
							<Icon name="search" />
						</button>

						<div className="navbar-mobile__logo">
							<a href={homeURL || '/'}>
								<Image src={logo} width={50} height={54} />
							</a>
						</div>

						<button
							type="button"
							aria-label="toggle login menu"
							className={cn(
								'login-btn',
								loginActive && 'menu-open',
							)}
							onClick={() => {
								setOpenMenu('login');
							}}
						>
							Portal Login
							<Icon name="caret-down" />
						</button>
					</div>
				)}
			</div>

			<div
				className={cn(
					'navbar-mobile__login',
					loginActive && 'navbar-mobile__login--menu-open',
					loginActive && menuTransition && 'closing',
				)}
			>
				{loginActive && (
					<Nav
						navItems={loginMenu}
						classNames={['login-menu']}
						vertical
						dropdownEnabled={false}
					/>
				)}
			</div>

			<div
				className={cn(
					'navbar-mobile__content',
					menuActive && 'navbar-mobile__content--menu-open',
					menuActive && menuTransition && 'closing',
				)}
			>
				{menuActive && (
					<>
						{ctaButton?.label && (
							<div className="navbar-mobile__content-cta">
								<Button
									{...ctaButton}
									classNames={['cta-btn']}
									type="secondary"
									size="sm"
								/>
							</div>
						)}
						{mobileMenu && (
							<AccordionNav
								navItems={mobileMenu}
								classNames={['mobile-menu']}
							/>
						)}
						{secondaryMenu && (
							<AccordionNav
								navItems={secondaryMenu}
								classNames={['secondary-menu']}
								vertical
								collapseEnabled
								dropdownEnabled={false}
							/>
						)}
						<div
							id="google_translate_element_mobile"
							className="notranslate"
						>
							<div>
								{translationLanguages && (
									<select
										value={selectedLanguage}
										onChange={(e) =>
											setSelectedLanguage(e.target.value)
										}
									>
										{translationLanguages.map((lang) => (
											<option
												key={lang.value}
												value={lang.value}
											>
												{lang.label}
											</option>
										))}
									</select>
								)}
							</div>
						</div>
						<SocialIcons
							showLogo={false}
							showFollowUs={false}
							icons={socialIcons}
						/>
					</>
				)}
			</div>
		</nav>
	);
};

export default NavMobile;
